import { useCallback } from "react";
import { listProjectItems, type ListedProjectItem } from "../../../api/content/authoring/processAuthoringApi";
import { readStepCatalogue, type StepCatalogue } from "../../../api/content/authoring/stepCatalogueApi";
import { listTalkSteps, type ListedTalkStep } from "../../../api/content/workflowsApi";
import { isAbortError } from "../../../api/http/httpClient";
import { toUiError, type UiError } from "../../../errors/uiError";
import type { ItemKind } from "../../../api/content/reviewDecoders";
import { useLoad, type LoadState } from "../../../shared/loading/useLoad";

/** An offer the form can do without: one the server cannot give does not block the form, which says why. */
export type OptionalOffer<Value> = { readonly status: "loaded"; readonly value: Value } | { readonly status: "failed"; readonly error: UiError };

/**
 * What the forms offer (AU-3): the project's items at `main` (tools, prompts, skills, label
 * lists, artifacts, steps, Workflows), the step catalogue from the server, and the talk steps a
 * review may offer. Without the catalogue the form offers only what the draft already names.
 */
export interface AuthoringOptions {
  /** The project the items belong to, which a form's library picker searches. */
  readonly projectId: string;
  readonly items: readonly ListedProjectItem[];
  /** The commit of `main` the items were listed at. */
  readonly mainVersion: string | null;
  readonly catalogue: OptionalOffer<StepCatalogue>;
  /** The talk steps the project can use at `main`, its pinned libraries' included (F105). */
  readonly talkSteps: OptionalOffer<readonly ListedTalkStep[]>;
}

async function optional<Value>(read: Promise<Value>): Promise<OptionalOffer<Value>> {
  try {
    return { status: "loaded", value: await read };
  } catch (caught: unknown) {
    if (isAbortError(caught)) {
      throw caught;
    }
    return { status: "failed", error: toUiError(caught) };
  }
}

async function loadOptions(projectId: string, signal: AbortSignal): Promise<AuthoringOptions> {
  const [listing, catalogue, talkSteps] = await Promise.all([
    listProjectItems(projectId, null, signal),
    optional(readStepCatalogue(projectId, signal)),
    optional(listTalkSteps(projectId, null, signal)),
  ]);
  return { projectId, items: listing.items, mainVersion: listing.version, catalogue, talkSteps };
}

export function useAuthoringOptions(projectId: string): { readonly state: LoadState<AuthoringOptions>; readonly reload: () => void } {
  const load = useCallback((signal: AbortSignal) => loadOptions(projectId, signal), [projectId]);
  const { state, reload } = useLoad(load);
  return { state, reload };
}

/** The project's items of one kind, by name. */
export function itemsOfKind(options: AuthoringOptions, kind: ItemKind): readonly ListedProjectItem[] {
  return options.items.filter((item) => item.kind === kind).sort((left, right) => left.name.localeCompare(right.name));
}

/** The listed item an item ID or `VersionRef` names, if it is at `main`. */
export function listedItem(options: AuthoringOptions, itemOrReference: string): ListedProjectItem | undefined {
  const itemId = itemOrReference.split("@")[0];
  return options.items.find((item) => item.itemId === itemId);
}
